var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { WalletNotSelectedError, AdapterState } from '@tronweb3/tronwallet-abstract-adapter';
import { TronLinkAdapter } from '@tronweb3/tronwallet-adapter-tronlink';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { WalletContext } from './useWallet.js';
import { useLocalStorage } from './useLocalStorage.js';
const initialState = {
    wallet: null,
    address: null,
    connected: false,
    adapter: null,
};
export const WalletProvider = function ({ children, adapters: adaptersPro = null, onError = (error) => console.error(error), onReadyStateChanged, onConnect, onDisconnect, onAccountsChanged, onChainChanged, onAdapterChanged, localStorageKey = 'tronAdapterName', autoConnect = true, disableAutoConnectOnLoad = false, }) {
    const [name, setName] = useLocalStorage(localStorageKey, null);
    const [{ wallet, connected, address, adapter }, setState] = useState(initialState);
    const [connecting, setConnecting] = useState(false);
    const [disconnecting, setDisconnecting] = useState(false);
    const isConnecting = useRef(false);
    const isDisconnecting = useRef(false);
    // set default supported adapters
    const adapters = useMemo(() => {
        if (adaptersPro === null) {
            return [new TronLinkAdapter()];
        }
        return adaptersPro;
    }, [adaptersPro]);
    const [wallets, setWallets] = useState(() => adapters.map((adapter) => ({
        adapter,
        state: adapter.state,
    })));
    useEffect(function () {
        setWallets((prevWallets) => adapters.map((adapter, index) => {
            const wallet = prevWallets[index];
            if (wallet && wallet.adapter === adapter && wallet.state === adapter.state) {
                return wallet;
            }
            return {
                adapter,
                state: adapter.state,
            };
        }));
        function handleStateChange() {
            setWallets((prevWallets) => {
                const index = prevWallets.findIndex((wallet) => wallet.adapter === this);
                if (index === -1) {
                    return prevWallets;
                }
                return prevWallets.map((wallet, idx) => {
                    if (idx === index) {
                        return Object.assign(Object.assign({}, wallet), { state: wallet.adapter.state });
                    }
                    return wallet;
                });
            });
        }
        adapters.forEach((adapter) => adapter.on('stateChanged', handleStateChange, adapter));
        return () => adapters.forEach((adapter) => adapter.off('stateChanged', handleStateChange, adapter));
    }, [adapters]);
    // Set state when choosen wallet changes
    useEffect(function () {
        const wallet = name && wallets.find((item) => item.adapter.name === name);
        if (wallet) {
            setState({
                wallet,
                adapter: wallet.adapter,
                connected: wallet.adapter.connected,
                address: wallet.adapter.address,
            });
        }
        else {
            setState(initialState);
        }
    }, [name, wallets]);
    const preAdapter = useRef(null);
    useEffect(function () {
        if (adapter !== preAdapter.current) {
            onAdapterChanged === null || onAdapterChanged === void 0 ? void 0 : onAdapterChanged(adapter);
            preAdapter.current = adapter;
        }
    }, [adapter, onAdapterChanged]);
    const handleConnect = useCallback(function (addr) {
        if (!adapter) {
            return setName(null);
        }
        setState((state) => (Object.assign(Object.assign({}, state), { connected: adapter.connected, address: adapter.address })));
        onConnect === null || onConnect === void 0 ? void 0 : onConnect(addr);
    }, [adapter, setName, onConnect]);
    const handleError = useCallback(function (error) {
        onError(error);
        return error;
    }, [onError]);
    const handleAccountChange = useCallback(function (address, preAddr) {
        setState((state) => (Object.assign(Object.assign({}, state), { address })));
        onAccountsChanged === null || onAccountsChanged === void 0 ? void 0 : onAccountsChanged(address, preAddr);
    }, [onAccountsChanged]);
    const handleDisconnect = useCallback(function () {
        onDisconnect === null || onDisconnect === void 0 ? void 0 : onDisconnect();
    }, [onDisconnect]);
    const handleReadyStateChanged = useCallback(function (readyState) {
        onReadyStateChanged === null || onReadyStateChanged === void 0 ? void 0 : onReadyStateChanged(readyState);
    }, [onReadyStateChanged]);
    const handleChainChanged = useCallback(function (chainData) {
        onChainChanged === null || onChainChanged === void 0 ? void 0 : onChainChanged(chainData);
    }, [onChainChanged]);
    useEffect(function () {
        if (adapter) {
            adapter.on('connect', handleConnect);
            adapter.on('error', handleError);
            adapter.on('accountsChanged', handleAccountChange);
            adapter.on('chainChanged', handleChainChanged);
            adapter.on('readyStateChanged', handleReadyStateChanged);
            adapter.on('disconnect', handleDisconnect);
            return () => {
                adapter.off('connect', handleConnect);
                adapter.off('error', handleError);
                adapter.off('accountsChanged', handleAccountChange);
                adapter.off('chainChanged', handleChainChanged);
                adapter.off('readyStateChanged', handleReadyStateChanged);
                adapter.off('disconnect', handleDisconnect);
            };
        }
    }, [
        adapter,
        handleConnect,
        handleError,
        handleAccountChange,
        handleChainChanged,
        handleReadyStateChanged,
        handleDisconnect,
    ]);
    // disconnect the previous when wallet changes
    useEffect(() => {
        return () => {
            adapter === null || adapter === void 0 ? void 0 : adapter.disconnect();
        };
    }, [adapter]);
    const hasManuallySetName = useRef(false);
    // auto connect
    useEffect(function () {
        const canAutoConnect = autoConnect && (!disableAutoConnectOnLoad || hasManuallySetName.current);
        if (isConnecting.current || !canAutoConnect || !adapter || adapter.state !== AdapterState.Disconnect) {
            return;
        }
        (function connect() {
            return __awaiter(this, void 0, void 0, function* () {
                isConnecting.current = true;
                setConnecting(true);
                try {
                    yield adapter.connect();
                }
                catch (error) {
                    // setName(null);
                }
                finally {
                    setConnecting(false);
                    isConnecting.current = false;
                }
            });
        })();
    }, [isConnecting, autoConnect, adapter, setName, disableAutoConnectOnLoad]);
    const select = useCallback((name) => {
        hasManuallySetName.current = true;
        setName(name);
    }, [setName]);
    const connect = useCallback(function () {
        return __awaiter(this, void 0, void 0, function* () {
            if (isConnecting.current || isDisconnecting.current || connected) {
                return;
            }
            if (!adapter)
                throw handleError(new WalletNotSelectedError());
            isConnecting.current = true;
            setConnecting(true);
            try {
                yield adapter.connect();
            }
            catch (error) {
                setName(null);
                throw error;
            }
            finally {
                setConnecting(false);
                isConnecting.current = false;
            }
        });
    }, [isConnecting, isDisconnecting, adapter, connected, handleError, setName]);
    const disconnect = useCallback(function () {
        return __awaiter(this, void 0, void 0, function* () {
            if (isDisconnecting.current)
                return;
            if (!adapter)
                return setName(null);
            isDisconnecting.current = true;
            setDisconnecting(true);
            try {
                yield adapter.disconnect();
                setName(null);
            }
            catch (error) {
                setName(null);
                throw error;
            }
            finally {
                setDisconnecting(false);
                isDisconnecting.current = false;
            }
        });
    }, [adapter, isDisconnecting, setName]);
    const signTransaction = useCallback(function (transaction, privateKey) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!adapter)
                throw handleError(new WalletNotSelectedError());
            return yield adapter.signTransaction(transaction, privateKey);
        });
    }, [adapter, handleError]);
    const multiSign = useCallback(function (...args) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!adapter)
                throw handleError(new WalletNotSelectedError());
            return yield adapter.multiSign(...args);
        });
    }, [adapter, handleError]);
    const signMessage = useCallback(function (message, privateKey) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!adapter)
                throw handleError(new WalletNotSelectedError());
            return yield adapter.signMessage(message, privateKey);
        });
    }, [adapter, handleError]);
    return (React.createElement(WalletContext.Provider, { value: {
            disableAutoConnectOnLoad,
            autoConnect,
            wallets,
            wallet,
            address,
            connecting,
            connected,
            disconnecting,
            select,
            connect,
            disconnect,
            signTransaction,
            multiSign,
            signMessage,
        } }, children));
};
//# sourceMappingURL=WalletProvider.js.map
